import { Suspense, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PageLoader } from '@customerio/pluma-components/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ThemeContextProvider } from '@/context/theme';
import Authenticated from '@/layouts/authenticated';
import DesignStudio, { DesignStudioNonBackgroundApp } from '@/pages/design-studio';
import NotFoundPage from '@/pages/not-found.page';
import { handleWorkspaceTransition } from '@/services/db/shared/workspace-cache-sync';
import { useLocation } from '@/services/router/compat';
import { getWorkspaceIdParam } from '@/services/router/workspace-id-param';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useStore } from '@nanostores/react';
import { accountStore } from '@/services/auth/store';
import { AccountState } from '@/services/auth/types';
import { requiresCardCheckout } from '@/pages/checkout/utils/promo-checkout-transforms';
import { AppBundle, AppBundleRoutes } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { Navigate } from '@/utils/navigate';
import { AssetLibrary } from './-route-pages';

type LazyPageProps = {
  children: ReactNode;
};

export function LazyPage({ children }: LazyPageProps) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * The redirect an app-entry gate renders, choosing the navigation primitive by what serves the URL the
 * account *arrived* on.
 *
 * A gate renders this **instead of** the app shell, so the embedded app is not mounted while the
 * redirect happens. `SafeNavigate` routes an embedded-bundle transition through the child-app
 * load/confirm handshake and parks it until `childAppLoaded` turns true — which cannot happen when the
 * shell is not rendered, so the transition parks forever and the account sees a blank page. That is why
 * an embedded-bundle entry navigates directly and lets the embedded-app history bridge resolve it.
 *
 * The usual reason to prefer `SafeNavigate` — that a plain navigate strands a booted embedded instance
 * on its old route — does not apply here, because the gate unmounts that instance rather than
 * navigating around it.
 *
 * A Hydra-owned entry URL has a null bundle, never parks, and keeps `SafeNavigate` so the transition
 * still goes through the navigation-guard machinery. Same distinction, and the same reason, as
 * `LastWorkspaceResolver` below (CON-7180).
 */
function gateRedirect(to: string, entryBundle: AppBundle | null) {
  if (entryBundle != null) {
    return <Navigate to={to} replace={true} />;
  }
  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * App-entry gates, checked before any authenticated page renders.
 *
 * Two independent conditions, deliberately kept as separate checks rather than folded into one:
 * they have different causes, different destinations, and either can be true without the other.
 * Merging them would change behaviour for an account that satisfies only one.
 *
 * Both are needed here because these routes do not boot the embedded app, so its equivalent
 * guards never run for them — an account reaching a Hydra-owned page would otherwise slip
 * straight past.
 */
type AppEntryGateProps = {
  children: ReactNode;
};

function AppEntryGate({ children }: AppEntryGateProps) {
  // Read the store rather than `useAuth`, which throws when the stores are not yet populated. This
  // gate sits above the layout that waits for auth, so it can and does render first; with a null
  // account both checks fall through and the layout handles its own loading, then the subscription
  // re-renders once the account lands.
  const account = useStore(accountStore);
  const isMasq = account?.is_masq === true;

  // Which app serves the URL the account arrived on — not the one it is being sent to. Derived here
  // rather than through `useActiveBundle` so this does not depend on the context provider sitting
  // above this gate. Re-derives when the account lands, because ownership is flag-gated.
  const location = useLocation();
  const entryBundle = getAppBundleFromUrl(location.pathname, account);

  // A promo account that has not added a card yet has no provisioned Ordway customer, so it is
  // in the app without a way to be billed. Send it to checkout on every entry, so the block is
  // durable across navigation and reloads (SPE-3567). Masquerading staff are exempt — they are
  // not the ones who would pay.
  if (!isMasq && account != null && requiresCardCheckout(account)) {
    return gateRedirect('/checkout', entryBundle);
  }

  // A cancelled account has no app to use.
  if (!isMasq && account?.account_state === AccountState.Canceled) {
    return gateRedirect('/account_canceled', entryBundle);
  }

  return children;
}

export function AuthenticatedLayout() {
  return (
    <AppEntryGate>
      <Authenticated />
    </AppEntryGate>
  );
}

export function WorkspaceRoot() {
  const location = useLocation();
  // Derive the workspace id from the pathname, not route params: switching
  // workspaces within the same app stays in the same bundle, which the
  // embedded-app history bridge syncs without a TanStack Router load. Route
  // matches/params go stale, so an effect keyed on params would never re-run
  // and the workspace-transition cache clear would silently never fire on the
  // most common switch path (CON-7180).
  const wsId = parseWorkspaceIdParam(getWorkspaceIdParam(location.pathname));
  const qc = useQueryClient();
  useEffect(() => {
    // A null id means the workspace segment is unresolved — the `/last`
    // sentinel before LastWorkspaceResolver redirects, or a non-numeric id.
    // Don't treat that as leaving the workspace: it would clear the current
    // workspace's cache only to immediately re-enter the same workspace once
    // `last` resolves. Wait for a real id so `previousWorkspaceId` always
    // tracks the last real workspace.
    if (wsId == null) return;
    handleWorkspaceTransition(qc, wsId);
  }, [qc, wsId]);
  return (
    <>
      <Outlet />
      <Suspense>
        <AssetLibrary />
      </Suspense>
    </>
  );
}

function parseWorkspaceIdParam(workspaceId: string | null | undefined): number | null {
  if (workspaceId == null) return null;
  const n = Number(workspaceId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

type LastWorkspaceResolverProps = {
  appBundle?: AppBundle;
  children: ReactNode;
};

function getLocationSuffix(location: { hash?: string; searchStr: string }) {
  const hash =
    location.hash == null || location.hash === '' || location.hash.startsWith('#')
      ? location.hash
      : `#${location.hash}`;
  return `${location.searchStr}${hash ?? ''}`;
}

export function LastWorkspaceResolver({ appBundle, children }: LastWorkspaceResolverProps) {
  const location = useLocation();
  const router = useRouter();
  const currentWorkspace = useCurrentWorkspace();

  // Derive the workspace id from the location pathname rather than route params.
  // Embedded apps (e.g. Design Studio) navigate within their own bundle without
  // triggering a TanStack Router load (see services/router/embedded-app-router-bridge),
  // so route matches/params go stale on a same-bundle navigation while the
  // location store stays in sync. Reading the pathname keeps the `last` sentinel
  // detectable so the redirect still fires (CON-7180).
  const workspaceId = getWorkspaceIdParam(location.pathname);

  const redirectPath = useMemo(() => {
    if (workspaceId !== 'last') {
      return null;
    }

    if (currentWorkspace == null) {
      return null;
    }

    if (appBundle != null) {
      const appPath = AppBundleRoutes[appBundle];
      const appPathWithSlash = `${appPath}/`;
      const appPathIndex = location.pathname.indexOf(appPathWithSlash);
      const subPath = appPathIndex !== -1 ? location.pathname.slice(appPathIndex + appPathWithSlash.length) : '';
      const workspacePath = router.buildLocation({
        to: '/workspaces/$workspaceId',
        params: { workspaceId: currentWorkspace.id },
      }).href;
      const redirectPath = [workspacePath, appPath.replace(/^\//, ''), subPath].filter(Boolean).join('/');
      return redirectPath + getLocationSuffix(location);
    }

    const workspacePath = router.buildLocation({
      to: '/workspaces/$workspaceId',
      params: { workspaceId: currentWorkspace.id },
    }).href;
    return location.pathname.replace('/workspaces/last', workspacePath) + getLocationSuffix(location);
  }, [workspaceId, currentWorkspace, location.hash, location.pathname, location.searchStr, appBundle, router]);

  if (redirectPath != null) {
    // Resolving the `last` sentinel to a real workspace id is a shell-level URL
    // normalization that must complete before the embedded app mounts. SafeNavigate
    // routes embedded-bundle transitions through the child-app load/confirm handshake,
    // but during this redirect the embedded app is not mounted yet (the resolver
    // renders this redirect instead of the app), so `childAppLoaded` stays false and
    // the transition parks forever. For embedded bundles, navigate directly and let
    // the embedded-app history bridge resolve it; hydra routes keep the SafeNavigate
    // transition machinery (their active bundle is null, so they never park).
    return appBundle != null ? (
      <Navigate to={redirectPath} replace={true} />
    ) : (
      <SafeNavigate to={redirectPath} options={{ shouldReplaceHistoryEntry: true }} />
    );
  }

  return children;
}

export function DesignStudioRoute() {
  return (
    <LastWorkspaceResolver appBundle={AppBundle.DesignStudio}>
      <DesignStudio />
    </LastWorkspaceResolver>
  );
}

export function EmbedDesignStudioRoute() {
  return <DesignStudioNonBackgroundApp />;
}

export function ThemedNotFoundRoute() {
  return (
    <ThemeContextProvider>
      <NotFoundPage />
    </ThemeContextProvider>
  );
}
